// 依能力顯示（SPEC #contract-app-ui，Eddy 2026-10-04；UX-11「按了一定回不支援的按鈕不准出現」）。
//
// 即時控制列（PTZ / 對講 / 警笛 / 燈）與設定頁只看能力，不看 shadow reported 有沒有值：
//   · 獨立設備：thing 的生效能力 capabilities.effective（façade 的 capabilities）——
//     kinio_ptz → PTZ、kinio_audio → 對講、kinio_siren → 警笛、kinio_floodlight → 燈（kinio_camera_light 是狀態燈 / 夜視，不出按鈕）。
//   · 通道（NVR / Station 的子設備）：沒有生效能力，看通道 shadow 的 kinio_channel.abilities {ptz, talk, light, siren}（façade channel_abilities）。
// 舊設備退回（App 三平台同一條）：設備從沒宣告能力（façade capabilities_declared = false：沒有 things.capabilities，
// 或 reported / effective 都空——舊韌體、nvrd / stationd 還沒補宣告）→ 退回原本的判斷：獨立設備看 reported_clusters；
// 通道沒報 abilities → PTZ 先畫、回不支援（422）再收，其他不畫。宣告過而 capabilities 空 = 能力上限全擋，什麼都不畫。
// 除了這個退回，reported_clusters 只拿來判資料來源（hasData，例 kinio_storage → 本地錄影主機）。
import type { VmsDevice } from './devices';

/** 通道的 kinio_channel.abilities（只取控制列用得到的四個）。 */
export interface ChannelAbilities {
  ptz: boolean;
  talk: boolean;
  light: boolean;
  siren: boolean;
}

/** 這台在即時控制列能出現的控制。 */
export interface ControlCaps {
  ptz: boolean;
  talk: boolean;
  siren: boolean;
  light: boolean;
}

/** 通道沒報 abilities（舊韌體）：PTZ 先畫（PtzPanel 回 PTZ_UNSUPPORTED 就收起），其他不畫——原本的做法。 */
const LEGACY_CHANNEL: ControlCaps = { ptz: true, talk: false, siren: false, light: false };

/** façade 的 capabilities（生效能力 `[{cluster, attrs}]`；舊格式字串陣列也收）→ cluster 名。不是陣列 → []。 */
export function effectiveClusters(c: unknown): string[] {
  if (!Array.isArray(c)) return [];
  const out: string[] = [];
  for (const x of c) {
    const name = typeof x === 'string' ? x : x && typeof x === 'object' ? (x as { cluster?: unknown }).cluster : undefined;
    if (typeof name === 'string' && name && !out.includes(name)) out.push(name);
  }
  return out;
}

/** façade 的 channel_abilities（或通道 shadow 的 kinio_channel.abilities）→ ChannelAbilities；沒有 → null。只認 true。 */
export function parseChannelAbilities(v: unknown): ChannelAbilities | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  return { ptz: o.ptz === true, talk: o.talk === true, light: o.light === true, siren: o.siren === true };
}

/**
 * 即時控制列的能力：通道看 abilities（沒報 → 舊做法）、獨立設備看生效能力（從沒宣告 → 看 reported_clusters）。
 * façade 還沒回來：通道照舊做法、獨立設備全沒有。
 */
export function controlCaps(d: Pick<VmsDevice, 'parentAid' | 'capabilities' | 'capsDeclared' | 'reportedClusters' | 'channelAbilities'>): ControlCaps {
  if (d.parentAid) {
    const a = d.channelAbilities;
    return a ? { ptz: a.ptz, talk: a.talk, siren: a.siren, light: a.light } : LEGACY_CHANNEL;
  }
  const have = new Set(d.capsDeclared ? (d.capabilities ?? []) : [...(d.capabilities ?? []), ...(d.reportedClusters ?? [])]);
  return { ptz: have.has('kinio_ptz'), talk: have.has('kinio_audio'), siren: have.has('kinio_siren'), light: have.has('kinio_floodlight') };
}

/** 資料來源判斷（回放順序、儲存健康）：生效能力或 shadow 報過這個 cluster。不拿來決定按鈕 / 設定出不出現。 */
export function hasData(d: Pick<VmsDevice, 'capabilities' | 'reportedClusters'> | undefined, cluster: string): boolean {
  return !!d && (!!d.capabilities?.includes(cluster) || !!d.reportedClusters?.includes(cluster));
}
